/**
 * SOLID Review
 * S — Strong. sole job: pick the next alive, non-suspended player after the roller in circular join
 *     order. Extracted from an EXACT byte-for-byte duplicate that lived independently inline in
 *     both ControlPanel.tsx and AppRoot.tsx — the same turn-rotation rule maintained twice, with
 *     no mechanism forcing them to stay in sync if one had ever been edited without the other.
 *     Strong (was Concern, in both files).
 * O — Good. a new "who's next" rule (e.g. a house-rule variant) changes this one function; both
 *     call sites automatically pick up the change.
 * L — N/A — no inheritance hierarchy; a single pure function.
 * I — Strong. One narrow export.
 * D — Strong. One import, INITIAL_LIVES from a domain sibling — same layer, no dependency on any
 *     other layer. Otherwise a pure function over primitives/plain objects.
 */
// Pure domain logic for turn-order selection: which player is being challenged
// next. Extracted from ControlPanel.tsx and AppRoot.tsx (this pass), where this
// exact function was independently duplicated in both files.
import { INITIAL_LIVES } from '../uml-components/primitives';

/**
 * Next alive, non-suspended player after rollerId in circular join order.
 * If every alive candidate is suspended (2-player game, opponent backgrounded),
 * fall back to the next alive player regardless of suspension: the decider must
 * always be a real player — the server's suspend→countdown→auto-believe machinery
 * owns a suspended decider, but only when it has an id to key on. Returning null
 * here made handleAnnounce send challengerId '' and poisoned the round (SUS-DEC).
 */
export function getChallengerId(
  players: ReadonlyArray<{ id: string }>,
  rollerId: string | null,
  lives: Readonly<Record<string, number>>,
  suspendedIds: ReadonlyArray<string> = []
): string | null {
  if (!rollerId || players.length < 2) return null;
  const rollerIdx = players.findIndex((p) => p.id === rollerId);
  if (rollerIdx === -1) return null;
  for (let i = 1; i < players.length; i++) {
    const candidate = players[(rollerIdx + i) % players.length]!;
    if ((lives[candidate.id] ?? INITIAL_LIVES) > 0 && !suspendedIds.includes(candidate.id)) {
      return candidate.id;
    }
  }
  for (let i = 1; i < players.length; i++) {
    const candidate = players[(rollerIdx + i) % players.length]!;
    if ((lives[candidate.id] ?? INITIAL_LIVES) > 0) {
      return candidate.id;
    }
  }
  return null;
}
